/**
 * @file: useMessageList.ts
 *
 * The conversation's message store — LOADING + RECONCILING the list:
 *   - initial load: fetch the first page for the resolved thread (owns the
 *     `loading` flag); converts the API DTO rows to domain `RemoteChatMsg`,
 *   - infinite scroll: `loadOlder` pages back, cursored on the server's
 *     `nextCursor` (the FE never derives the cursor from a message),
 *   - reconnect resync: backfill what was missed during a connection gap,
 *   - the optimistic-send lifecycle, driven entirely off the coordinator bus
 *     (the pipeline never calls in): `willSendMsg$` inserts a `LocalChatMsg`,
 *     `messageSent$` reconciles the temp to a `RemoteChatMsg` (or flips it to
 *     `failed`), `beforeResend$` drops a failed bubble before a retry,
 *   - the live message-level room events: it OWNS the list, so it subscribes
 *     to `message.new` / `.edited` / `.deleted` / `.read` itself and applies
 *     them (no central router fanning events out).
 *
 * It owns NO view state. The message list itself (`messages$`) is the
 * provider's single source of truth, passed in. It talks to the rest of the
 * conversation only through the coordinator's event bus — emitting inserts /
 * resyncs, and subscribing to the send-outcome, play-audio + jump-to-quoted
 * intents (so the pipeline, scroll, readers overlay, etc. never reach into the
 * store). Mutations go through immer.
 */

import { ChatMessageReadEvent } from '@euler/app/flows/chat/lib/session/chatEvents';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { chatMessageFromEvent } from '@euler/app/flows/chat/utils/chatMessageFromEvent';
import { remoteChatMsgFromItem } from '@euler/app/flows/chat/utils/remoteChatMsgFromItem';
import {
  ChatMessagePayload,
  ChatMessageReader,
  ChatMsg,
  ChatThreadMessageItem,
  ChatUserMessagePayload,
  RemoteChatMsg,
} from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { idEquals, onErrorIgnore } from '@euler/utils';
import { useCreation } from '@euler/utils/hooks';
import { Draft, produce } from 'immer';
import { useCallback, useEffect, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ChatConversationHandle, hasThread, isBaked } from '../handle';
import { ConversationCoordinator } from './useConversationCoordinator';

/** Messages fetched per page — initial load and each older page. */
const DEFAULT_PAGE_SIZE = 50;

/** Dedup key: a local message has only a clientMessageId; a remote one
 * has the canonical messageId. */
const dedupKey = (m: ChatMsg): string =>
  m.kind === 'local' ? m.clientMessageId : m.messageId;

interface MessageListDeps {
  conversationHandle: ChatConversationHandle;
  /** Provider-owned single source of truth for the list (the store mutates). */
  messages$: BehaviorSubject<ChatMsg[]>;
  /** The event bus — the store emits inserts/resyncs and subscribes to the
   * play-audio + jump-to-quoted intents. */
  coordinator: ConversationCoordinator;
  /** Messages per page (initial + older). Defaults to {@link DEFAULT_PAGE_SIZE}. */
  pageSize?: number;
}

export function useMessageList(deps: MessageListDeps) {
  const {
    conversationHandle,
    messages$,
    coordinator,
    pageSize = DEFAULT_PAGE_SIZE,
  } = deps;
  const { chatService } = useServiceFactory();
  const { me, wsClient } = useChatSession();
  // The viewer's loadable thread, or null when there's nothing to load yet —
  // a bare room (no room), or a baked room with no per-user thread. The first
  // send materialises one and this flips to a real id (re-running the load).
  const threadId = hasThread(conversationHandle)
    ? conversationHandle.threadId
    : null;
  const roomId = isBaked(conversationHandle) ? conversationHandle.roomId : null;

  const loading$ = useCreation(() => new BehaviorSubject(true), []);

  const seenIdsRef = useRef<Set<string>>(new Set());

  // Older-page (infinite-scroll) state. `loadingOlder$` is a subject so the
  // footer spinner re-renders only the list, not the provider. `hasMore`, the
  // in-flight flag, and the pagination cursor are refs — read synchronously,
  // never rendered. `nextCursorRef` is the server's `nextCursor` (the oldest
  // recipient-row id), so the FE never derives the cursor from a message.
  const loadingOlder$ = useCreation(() => new BehaviorSubject(false), []);
  const loadingOlderRef = useRef(false);
  const hasMoreRef = useRef(true);
  const nextCursorRef = useRef<string | null>(null);

  // Apply an in-place immer recipe to the list and publish the next value.
  const update = useCallback(
    (recipe: (draft: Draft<ChatMsg[]>) => void) => {
      messages$.next(produce(messages$.getValue(), recipe));
    },
    [messages$],
  );

  const addMessage = useCallback(
    (msg: ChatMsg) => {
      const key = dedupKey(msg);
      if (seenIdsRef.current.has(key)) {
        // Duplicate. The one useful case: the server echo of an OWN message
        // we reconciled off the send response (which carries no recipient
        // id). Backfill the now-known recipient `id` so a later jump-to-quoted
        // can anchor on it.
        if (msg.kind === 'remote' && msg.id) {
          update(draft => {
            const m = draft.find(
              it => it.kind === 'remote' && it.messageId === msg.messageId,
            );
            if (m?.kind === 'remote' && !m.id) m.id = msg.id;
          });
        }
        return;
      }
      seenIdsRef.current.add(key);
      // Inverted list: data is newest-first, so a new message goes to the
      // FRONT (index 0 = visual bottom).
      update(draft => {
        draft.unshift(msg as Draft<ChatMsg>);
      });
      // Announce the real insert on the bus; the scroll follows or counts.
      coordinator.messageInserted$.next(msg);
    },
    [update, coordinator.messageInserted$],
  );

  // Promote an optimistic send to its server-canonical message. If the WS
  // echo already inserted the canonical row (it raced ahead of the send
  // response), drop the optimistic temp to avoid a duplicate; otherwise
  // REPLACE the local with a remote message (the local → remote flip).
  const reconcileSent = useCallback(
    (
      clientMessageId: string,
      result: {
        messageId: string;
        roomId?: string;
        payload?: ChatUserMessagePayload;
      },
    ) => {
      const echoed = seenIdsRef.current.has(result.messageId);
      seenIdsRef.current.delete(clientMessageId);
      if (echoed) {
        update(draft => {
          const i = draft.findIndex(
            m => m.kind === 'local' && m.clientMessageId === clientMessageId,
          );
          if (i >= 0) draft.splice(i, 1);
        });
        return;
      }
      seenIdsRef.current.add(result.messageId);
      update(draft => {
        const i = draft.findIndex(
          m => m.kind === 'local' && m.clientMessageId === clientMessageId,
        );
        if (i < 0) return;
        const local = draft[i];
        draft[i] = {
          kind: 'remote',
          messageId: result.messageId,
          // The send response carries no recipient-row id (the id-bearing WS
          // echo is deduped away), so an own just-sent message has none —
          // fine, you don't jump-to-context your own fresh message.
          id: '',
          roomId: result.roomId ?? local.roomId,
          authorId: local.authorId,
          authorType: local.authorType,
          authorRole: local.authorRole,
          authorName: local.authorName,
          authorAvatar: local.authorAvatar,
          // Swap the local-preview payload (e.g. a media bubble's local file
          // URI) for the uploaded one once it's known.
          payloadType: result.payload?.type ?? local.payloadType,
          payload: result.payload ?? local.payload,
          quoteMessageId: local.quoteMessageId,
          quoteSnapshot: local.quoteSnapshot,
          createdAt: local.createdAt,
          editedAt: null,
          deletedAt: null,
        } as Draft<RemoteChatMsg>;
      });
    },
    [update],
  );

  // If the upload already succeeded (only the persist failed), `finalPayload`
  // carries the uploaded payload so a retry re-sends the real URL rather than
  // the local preview.
  const markSendFailed = useCallback(
    (clientMessageId: string, finalPayload?: ChatUserMessagePayload) => {
      update(draft => {
        const m = draft.find(
          it => it.kind === 'local' && it.clientMessageId === clientMessageId,
        );
        if (m?.kind !== 'local') return;
        if (finalPayload) {
          m.payload = finalPayload;
          m.payloadType = finalPayload.type;
        }
        m.status = 'failed';
      });
    },
    [update],
  );

  const applyEdited = useCallback(
    (
      messageId: string,
      payload: ChatMessagePayload | undefined,
      // The server's canonical edit time (from the WS echo). Omitted for the
      // optimistic local apply, which stamps client-now until the echo lands.
      editedAt?: string,
    ) => {
      update(draft => {
        const m = draft.find(
          it => it.kind === 'remote' && it.messageId === messageId,
        );
        if (m?.kind !== 'remote') return;
        m.payload = payload ?? m.payload;
        m.editedAt = editedAt ?? new Date().toISOString();
      });
    },
    [update],
  );

  const applyDeleted = useCallback(
    (messageId: string) => {
      update(draft => {
        const m = draft.find(
          it => it.kind === 'remote' && it.messageId === messageId,
        );
        if (m?.kind === 'remote') m.deletedAt = new Date().toISOString();
      });
    },
    [update],
  );

  // A reaction was added/removed (from the WS echo — fires for every member
  // INCLUDING the actor, so reactions aren't applied optimistically; the echo
  // is the single source). Fold it into the message's per-emoji summary;
  // `byMe` flips `reactedByMe` only when the actor is the viewer.
  const applyReactionChanged = useCallback(
    (messageId: string, emoji: string, added: boolean, byMe: boolean) => {
      update(draft => {
        const m = draft.find(
          it => it.kind === 'remote' && it.messageId === messageId,
        );
        if (m?.kind !== 'remote') return;
        if (!m.reactions) m.reactions = [];
        const existing = m.reactions.find(r => r.emoji === emoji);
        if (added) {
          if (existing) {
            existing.count += 1;
            if (byMe) existing.reactedByMe = true;
          } else {
            m.reactions.push({ emoji, count: 1, reactedByMe: byMe });
          }
        } else if (existing) {
          existing.count -= 1;
          if (byMe) existing.reactedByMe = false;
          if (existing.count <= 0) {
            m.reactions = m.reactions.filter(r => r.emoji !== emoji);
          }
        }
      });
    },
    [update],
  );

  // Read receipt: someone read up to `upToMessageId`. Flip MY sent messages
  // at-or-before that horizon to "read" and record who. Ignores our own read
  // (multi-device sync, not a receipt). Only confirmed (remote) messages can
  // carry a read receipt.
  const applyReadReceipt = useCallback(
    (event: ChatMessageReadEvent) => {
      const readerId = event.platformUserId;
      // Our own read (multi-device sync) isn't a receipt to render.
      if (idEquals(readerId, me.platformUserId)) return;
      const upTo = event.body.upToMessageId;
      const reader: ChatMessageReader = {
        platformUserId: readerId,
        appId: event.appId,
        appScope: event.appScope,
        name: event.authorName,
        avatar: event.authorAvatar ?? null,
        readAt: event.body.readAt,
      };
      update(draft => {
        for (const m of draft) {
          if (m.kind !== 'remote') continue;
          // Only my own messages at-or-before the read horizon. Message ids
          // are ULID-sortable, so a string compare gives chronological "<=".
          if (!idEquals(m.authorId, me.platformUserId)) continue;
          if (m.messageId > upTo) continue;
          if (!m.readers) m.readers = [];
          const already = m.readers.some(r =>
            idEquals(r.platformUserId, reader.platformUserId),
          );
          if (!already) m.readers.push(reader);
          m.readByCount = Math.max(m.readByCount ?? 0, m.readers.length);
        }
      });
    },
    [update, me],
  );

  // Reconnect resync: backfill messages missed during a connection gap. The
  // gap messages are newer than what we have, so prepend then sort DESC to
  // absorb out-of-order arrivals.
  const mergeResync = useCallback(
    (items: ChatThreadMessageItem[]) => {
      const fresh = items
        .filter(it => !seenIdsRef.current.has(it.messageId))
        .map(remoteChatMsgFromItem);
      if (fresh.length === 0) return;
      for (const m of fresh) seenIdsRef.current.add(m.messageId);
      update(draft => {
        draft.unshift(...(fresh as Draft<ChatMsg>[]));
        draft.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      });
      // The scroll follows down if the reader is at the bottom.
      coordinator.messagesResynced$.next();
    },
    [update, coordinator.messagesResynced$],
  );

  // Seed the list from the initial server fetch. The response `nextCursor`
  // seeds the infinite-scroll cursor + guard.
  const setInitialMessages = useCallback(
    (items: ChatThreadMessageItem[], nextCursor: string | null) => {
      const msgs = items.map(remoteChatMsgFromItem);
      seenIdsRef.current = new Set(msgs.map(m => m.messageId));
      nextCursorRef.current = nextCursor;
      hasMoreRef.current = nextCursor != null;
      loadingOlderRef.current = false;
      loadingOlder$.next(false);
      messages$.next(msgs);
    },
    [messages$, loadingOlder$],
  );

  // Load the next older page — triggered by `onEndReached` when the user
  // scrolls toward the top of history (the END of the inverted list).
  // Cursored on the server's `nextCursor`. Concurrent calls are dropped, and
  // it stops once the server reports no more.
  const loadOlder = useCallback(async () => {
    if (!threadId || loadingOlderRef.current || !hasMoreRef.current) return;
    const cursor = nextCursorRef.current;
    if (!cursor) return;
    loadingOlderRef.current = true;
    loadingOlder$.next(true);
    try {
      const result = await chatService.listThreadMessages(threadId, {
        before: cursor,
        limit: pageSize,
      });
      const older = result.items
        .filter(it => !seenIdsRef.current.has(it.messageId))
        .map(remoteChatMsgFromItem);
      older.forEach(m => seenIdsRef.current.add(m.messageId));
      if (older.length > 0) {
        update(draft => {
          draft.push(...(older as Draft<ChatMsg>[]));
        });
      }
      nextCursorRef.current = result.nextCursor;
      hasMoreRef.current = result.nextCursor != null;
    } catch (e) {
      console.error('load older messages failed', e);
    } finally {
      loadingOlderRef.current = false;
      loadingOlder$.next(false);
    }
  }, [threadId, chatService, update, loadingOlder$, pageSize]);

  // Initial load: fetch the first page for the (already-resolved) thread. A
  // bare handle has no threadId yet — nothing to load until the first send
  // materialises the room.
  const loadInitial = useCallback(async () => {
    if (!threadId) {
      loading$.next(false);
      return;
    }
    try {
      loading$.next(true);
      // Inverted list: keep the server's DESC order (newest first); data[0]
      // renders at the visual bottom, so no initial scroll-to-bottom needed.
      const result = await chatService.listThreadMessages(threadId, {
        limit: pageSize,
      });
      setInitialMessages(result.items, result.nextCursor);
    } catch (e) {
      console.error('load messages failed', e);
    } finally {
      loading$.next(false);
    }
  }, [chatService, threadId, setInitialMessages, pageSize, loading$]);

  // Open / switch: load the conversation. The provider remounts per room
  // (mobile nav push; desktop keys the embed), so scroll state starts fresh —
  // the inverted list mounts at offset 0 (the bottom).
  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  // Resync on WS reconnect: the transport has no server-side replay, so a
  // broadcast during a heartbeat-timeout gap is lost. When the connection
  // transitions back to `connected` from a non-connected state, refetch the
  // newest page and merge anything missed. The initial open (prev=undefined)
  // is skipped — `loadInitial` already covers it.
  useEffect(() => {
    if (!threadId) return;
    let prevState: string | undefined = undefined;
    const sub = wsClient.connectionState$.subscribe(state => {
      const wasNotConnected =
        prevState !== undefined && prevState !== 'connected';
      prevState = state;
      if (state !== 'connected' || !wasNotConnected) return;
      chatService
        .listThreadMessages(threadId, { limit: pageSize })
        .then(result => mergeResync(result.items))
        .catch(onErrorIgnore);
    });
    return () => sub.unsubscribe();
  }, [threadId, chatService, wsClient, mergeResync, pageSize]);

  // Remove a failed bubble so a retry can re-insert a fresh optimistic one.
  const dropFailed = useCallback(
    (clientMessageId: string | undefined) => {
      if (clientMessageId) seenIdsRef.current.delete(clientMessageId);
      update(draft => {
        const i = draft.findIndex(
          m => m.kind === 'local' && m.clientMessageId === clientMessageId,
        );
        if (i >= 0) draft.splice(i, 1);
      });
    },
    [update],
  );

  // Page history back until a (quoted) message appears, or there's no more.
  // Reuses `loadOlder` (sequential awaits) so there's one pagination path.
  const ensureMessageLoaded = useCallback(
    async (messageId: string): Promise<boolean> => {
      const has = () =>
        messages$
          .getValue()
          .some(m => m.kind === 'remote' && m.messageId === messageId);
      let attempts = 0;
      while (!has() && hasMoreRef.current && attempts < 10) {
        await loadOlder();
        attempts += 1;
      }
      return has();
    },
    [messages$, loadOlder],
  );

  // First play of a received voice → stamp it played locally (clears the red
  // dot instantly) and persist on the server. Received audio is always remote.
  const markAudioPlayed = useCallback(
    (message: ChatMsg) => {
      if (message.kind !== 'remote' || message.audioPlayedAt) return;
      const mid = message.messageId;
      const now = new Date().toISOString();
      update(draft => {
        const m = draft.find(
          it => it.kind === 'remote' && it.messageId === mid,
        );
        if (m?.kind === 'remote') m.audioPlayedAt = now;
      });
      chatService.markAudioPlayed(mid).catch(onErrorIgnore);
    },
    [update, chatService],
  );

  // ── handle the bubble intents the coordinator routes to the store

  const {
    audioPlayRequested$,
    editSubmitted$,
    deleteRequested$,
    reactRequested$,
    scrollToMessageRequested$,
    scrollToLoadedMessage$,
    willSendMsg$,
    messageSent$,
    beforeResend$,
  } = coordinator;

  useEffect(() => {
    const sub = audioPlayRequested$.subscribe(markAudioPlayed);
    return () => sub.unsubscribe();
  }, [audioPlayRequested$, markAudioPlayed]);

  // A reaction tap (pill / chip): just toggle on the server. The room's
  // `reaction.changed` echo (which the actor also receives) is what folds it
  // into the list, so the count is applied exactly once.
  useEffect(() => {
    const sub = reactRequested$.subscribe(({ message, emoji }) => {
      if (message.kind !== 'remote') return;
      chatService
        .toggleReaction(message.messageId, emoji)
        .catch(e => console.error('toggle reaction failed', e));
    });
    return () => sub.unsubscribe();
  }, [reactRequested$, chatService]);

  // The menu's 撤回: soft-delete the bubble instantly (tombstone), then
  // persist. On failure, un-delete so the message reappears. The WS
  // `message.deleted` echo is idempotent (already deleted).
  useEffect(() => {
    const sub = deleteRequested$.subscribe(message => {
      if (message.kind !== 'remote') return;
      const mid = message.messageId;
      applyDeleted(mid);
      chatService.deleteMessage(mid).catch(e => {
        console.error('delete message failed', e);
        update(draft => {
          const m = draft.find(
            it => it.kind === 'remote' && it.messageId === mid,
          );
          if (m?.kind === 'remote') m.deletedAt = null;
        });
      });
    });
    return () => sub.unsubscribe();
  }, [deleteRequested$, applyDeleted, chatService, update]);

  // The composer submits an edit: apply the new payload optimistically, then
  // persist. On failure, restore the previous payload + edited-at so the
  // bubble reverts. The WS `message.edited` echo re-applies the same payload.
  useEffect(() => {
    const sub = editSubmitted$.subscribe(({ messageId, payload }) => {
      const prev = messages$
        .getValue()
        .find(
          (m): m is RemoteChatMsg =>
            m.kind === 'remote' && m.messageId === messageId,
        );
      // The target vanished or was deleted (e.g. a remote delete landed while
      // the composer sat in edit mode) — drop the edit rather than resurrect it.
      if (!prev || prev.deletedAt) return;
      const prevPayload = prev.payload;
      const prevEditedAt = prev.editedAt ?? null;
      applyEdited(messageId, payload);
      chatService.editMessage(messageId, payload).catch(e => {
        console.error('edit message failed', e);
        if (!prevPayload) return;
        update(draft => {
          const m = draft.find(
            it => it.kind === 'remote' && it.messageId === messageId,
          );
          if (m?.kind === 'remote') {
            m.payload = prevPayload;
            m.editedAt = prevEditedAt;
          }
        });
      });
    });
    return () => sub.unsubscribe();
  }, [editSubmitted$, applyEdited, chatService, update, messages$]);

  // The send pipeline emits the optimistic bubble; the store owns the list, so
  // it inserts it (which also announces the insert on `messageInserted$`).
  useEffect(() => {
    const sub = willSendMsg$.subscribe(addMessage);
    return () => sub.unsubscribe();
  }, [willSendMsg$, addMessage]);

  // The send pipeline announces each send's outcome on the bus; the store owns
  // the list, so it applies it — reconcile the optimistic bubble to its server
  // message on success, flip it to `failed` (tap to retry) otherwise.
  useEffect(() => {
    const sub = messageSent$.subscribe(outcome => {
      if (outcome.status === 'sent') {
        reconcileSent(outcome.clientMessageId, {
          messageId: outcome.messageId,
          roomId: outcome.roomId,
          payload: outcome.payload,
        });
      } else {
        markSendFailed(outcome.clientMessageId, outcome.finalPayload);
      }
    });
    return () => sub.unsubscribe();
  }, [messageSent$, reconcileSent, markSendFailed]);

  // A retry is about to re-insert a fresh optimistic bubble; drop the failed
  // one first.
  useEffect(() => {
    const sub = beforeResend$.subscribe(dropFailed);
    return () => sub.unsubscribe();
  }, [beforeResend$, dropFailed]);

  // Jump-to-quoted: page the message in (the store's job), then announce it's
  // loaded so the scroll can jump + highlight.
  useEffect(() => {
    const sub = scrollToMessageRequested$.subscribe(messageId => {
      void ensureMessageLoaded(messageId).then(found => {
        if (found) scrollToLoadedMessage$.next(messageId);
      });
    });
    return () => sub.unsubscribe();
  }, [scrollToMessageRequested$, scrollToLoadedMessage$, ensureMessageLoaded]);

  // The store owns the list, so it listens for the message-level room events
  // that change it and applies them — no central router fanning these out.
  // (Marking the thread read while viewing is presence's job, not the
  // store's — see useConversationPresence.)
  useEffect(() => {
    if (!roomId) return;
    const sub = wsClient.onRoomEvent(roomId).subscribe(event => {
      if (event.kind === 'message.new') {
        addMessage(chatMessageFromEvent(event, roomId));
      } else if (event.kind === 'message.edited') {
        applyEdited(
          event.body.messageId,
          event.body.payload,
          event.body.editedAt,
        );
      } else if (event.kind === 'message.deleted') {
        applyDeleted(event.body.messageId);
      } else if (event.kind === 'message.read') {
        applyReadReceipt(event);
      } else if (event.kind === 'reaction.changed') {
        applyReactionChanged(
          event.body.messageId,
          event.body.emoji,
          event.body.added,
          idEquals(event.platformUserId, me.platformUserId),
        );
      }
    });
    return () => sub.unsubscribe();
  }, [
    roomId,
    wsClient,
    me,
    addMessage,
    applyEdited,
    applyDeleted,
    applyReadReceipt,
    applyReactionChanged,
  ]);

  return {
    loading$,
    loadingOlder$,
    loadOlder,
  };
}

export type MessageListStore = ReturnType<typeof useMessageList>;
